import type { StartupDataField, StartupDataRecord } from '@ideoz/core';

/** Fields Nest writes on every record; shown, never edited. */
export const SYSTEM_FIELDS = ['createdAt', 'updatedAt', 'createdBy'] as const;

/**
 * The table's columns: `id`, then the entity's catalog fields, then any other field a record
 * has (a prototype that knows more fields than the catalog), system fields last.
 */
export function columnsFor(fields: StartupDataField[], records: StartupDataRecord[]): string[] {
  const columns = ['id'];
  const add = (name: string) => {
    if (!columns.includes(name) && !(SYSTEM_FIELDS as readonly string[]).includes(name))
      columns.push(name);
  };
  fields.forEach((f) => add(f.name));
  records.forEach((r) => Object.keys(r).forEach(add));
  return [...columns, 'updatedAt'];
}

/** A value as the table shows it. */
export function displayValue(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** Objects and lists are shown but edited in the prototype, not in a table cell. */
export function isEditable(column: string, value: unknown): boolean {
  if (column === 'id' || (SYSTEM_FIELDS as readonly string[]).includes(column)) return false;
  return value === undefined || value === null || typeof value !== 'object';
}

/**
 * What the founder typed, as the field's own type: a number stays a number, yes/no stays a
 * boolean. `error` names the type the text did not fit (`startupData.fieldNumber` /
 * `startupData.fieldBoolean`).
 */
export function typedValue(
  text: string,
  previous: unknown,
  field?: StartupDataField
): { value: unknown } | { error: 'number' | 'boolean' } {
  const kind =
    typeof previous === 'number' || field?.type === 'number'
      ? 'number'
      : typeof previous === 'boolean' || field?.type === 'boolean'
        ? 'boolean'
        : 'text';
  if (kind === 'number') {
    const trimmed = text.trim().replace(/,/g, '');
    if (trimmed === '') return { value: null };
    const n = Number(trimmed);
    return Number.isFinite(n) ? { value: n } : { error: 'number' };
  }
  if (kind === 'boolean') {
    const t = text.trim().toLowerCase();
    if (['true', 'yes', 'on', '1'].includes(t)) return { value: true };
    if (['false', 'no', 'off', '0', ''].includes(t)) return { value: false };
    return { error: 'boolean' };
  }
  return { value: text };
}

/**
 * Prototype names for "Used by", each once: two prototypes with the same title read as
 * "Sales Operations, Sales Operations" (testing, 22 Sep 2026); now "Sales Operations (2)".
 */
export function usedByNames(titles: string[]): string {
  const counts = new Map<string, number>();
  for (const title of titles) counts.set(title, (counts.get(title) ?? 0) + 1);
  return [...counts].map(([title, n]) => (n > 1 ? `${title} (${n})` : title)).join(', ');
}

/**
 * The roles to offer for an account: `user` and `admin` (what generated prototypes check), then
 * every role an account of the startup has, then the current value, each once.
 */
export function knownRoles(usedRoles: string[], current?: string): string[] {
  const roles = ['user', 'admin', ...usedRoles, ...(current ? [current] : [])]
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(roles)];
}
